import { useCallback } from 'react';
import { useToggle } from 'react-use';

import { DocumentSendError, PueFault } from '@dreryk/egabinet-ezla-frontend-api';
import { Visit, VisitMedicalLeaveCreateDraftCommand } from '@dreryk/egabinet-visit-frontend-api';

import { useDataSyncing } from '@dreryk/command-query-react-common';
import { FormContextValues } from '@dreryk/react-common';
import { ActionBar, ButtonVariant, DataSyncWaitingEventInterceptor, useNavTabsContext } from '@dreryk/react-components';

import { useAccountAuthorizationModal } from '~libs.desktop.shared.account';

import { useVisitDocumentContext } from '../../../../../common/visitDocument/VisitDocumentContext';
import { commonTranslationKeys, medicalLeaveTranslationKeys } from '../../../../../translations';
import { MedicalLeavePanelNavParams } from '../../../MedicalLeavePanel';
import { MedicalLeaveFormBean } from '../../medicalLeave.types';
import { PassportIdentityDocumentValidModal } from '../../sections/patientData/identityDocument/passport/valid/PassportIdentityDocumentValidModal';
import { PassportIdentityDocumentValidModalLoader } from '../../sections/patientData/identityDocument/passport/valid/PassportIdentityDocumentValidModalLoader';

import { useMedicalLeaveApproveCommand } from './MedicalLeaveSubmitAction.hooks';

type MedicalLeaveSubmitActionProps = {
   readonly visit: Visit;
   readonly formController: FormContextValues<MedicalLeaveFormBean>;
   readonly onPueFault: (fault: PueFault) => void;
   readonly onZlaDocumentSendFault: (zlaDocumentSendErrors: DocumentSendError[]) => void;
};

const MedicalLeaveSubmitAction = (props: MedicalLeaveSubmitActionProps): JSX.Element => {
   const { executeVisitDocumentCommand } = useVisitDocumentContext();
   const { openAccountAuthorizationModal, renderAccountAuthorizationnModal } = useAccountAuthorizationModal();
   const [showPassportIdentityDocumentModal, togglePassportIdentityDocumentModal] = useToggle(false);
   const { changeTab } = useNavTabsContext<
      MedicalLeavePanelNavParams.LEAVE_FORM | MedicalLeavePanelNavParams.LEAVES_HISTORY
   >();

   const { executeAsync: submitMedicalLeave } = useMedicalLeaveApproveCommand({
      onSuccess: () => {
         props.formController.reset();
         changeTab(MedicalLeavePanelNavParams.LEAVES_HISTORY);
      },
      onPueFault: props.onPueFault,
      onZlaDocumentSendFault: props.onZlaDocumentSendFault,
      onAuthenticationRequiredFault: openAccountAuthorizationModal,
      onValidPassportIdentityDocumentRequiredFault: togglePassportIdentityDocumentModal,
   });

   const handleSubmitMedicalLeave = useCallback(
      async (formData: MedicalLeaveFormBean): Promise<unknown> => {
         return executeVisitDocumentCommand(VisitMedicalLeaveCreateDraftCommand, async (externalId) =>
            submitMedicalLeave({ formData: formData, externalId: externalId })
         );
      },
      [submitMedicalLeave, executeVisitDocumentCommand]
   );

   return (
      <>
         <DataSyncWaitingEventInterceptor waitUntil={useDataSyncing()}>
            {(waiting) => (
               <ActionBar.PrimaryButton
                  label={commonTranslationKeys.submit}
                  variant={ButtonVariant.PRIMARY}
                  onClick={props.formController.handleSubmit(handleSubmitMedicalLeave)}
                  disabled={waiting}
                  testId='submit'
                  blockingOperation={medicalLeaveTranslationKeys.common.submittingMedicalLeave}
               />
            )}
         </DataSyncWaitingEventInterceptor>
         {renderAccountAuthorizationnModal({
            onAuthorization: props.formController.handleSubmit(handleSubmitMedicalLeave),
         })}
         {showPassportIdentityDocumentModal ? (
            <PassportIdentityDocumentValidModalLoader patientId={props.visit.patient.id}>
               {({ identityDocument }) => (
                  <PassportIdentityDocumentValidModal
                     identityDocument={identityDocument}
                     patientId={props.visit.patient.id}
                     onValidIdentityDocumentEntered={props.formController.handleSubmit(handleSubmitMedicalLeave)}
                     onSuccess={togglePassportIdentityDocumentModal}
                     onCancel={togglePassportIdentityDocumentModal}
                  />
               )}
            </PassportIdentityDocumentValidModalLoader>
         ) : null}
      </>
   );
};

export { MedicalLeaveSubmitAction };
